import React from "react";
import { faCheck, faTimes, faWarning } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useSelector } from "react-redux";
import Typography from "@material-ui/core/Typography";
import styled from "styled-components/macro";
import {
  getConsignmentById,
  getNeverEverEligibilityStatusByConsignmentId,
} from "selectors";

import { EMPTY_OBJECT } from "lib";
import { Row, Column } from "components/Layout";
import { formatUTCToLocalDateTimeString } from "lib/timeFormats";
import { SmallText } from "components/Text";
import {
  NeverEverEligibilityStatus,
  NeverEverEligibilityStatusDisplayNameMap,
} from "constants/greenhams";
import { Tooltip } from "components/Form/FormikControls/Tooltip";

const NeverEverStatusIcon = styled(FontAwesomeIcon)`
  font-size: 20px;
  width: auto;
  color: ${({ theme, color }) => theme.colors[color]};
  margin: 0 ${({ theme }) => theme.space[1]}px;
`;

export const EligibleIcon = styled(NeverEverStatusIcon).attrs({
  icon: faCheck,
  color: "successGreen",
})``;

export const IneligibleIcon = styled(NeverEverStatusIcon).attrs({
  icon: faTimes,
  color: "errorRed",
})``;

export const EligibilityErrorIcon = styled(NeverEverStatusIcon).attrs({
  icon: faWarning,
  color: "warningOrange",
})``;

export interface EligibilityStatusProps {
  eligibilityStatus: NeverEverEligibilityStatus;
  showTextStatus?: boolean;
}

const IconTextMap = {
  [NeverEverEligibilityStatus.ELIGIBLE]: {
    icon: EligibleIcon,
    text: "PIC is accredited",
  },
  [NeverEverEligibilityStatus.INELIGIBLE]: {
    icon: IneligibleIcon,
    text: "PIC is not accredited",
  },
  [NeverEverEligibilityStatus.ELIGIBILITY_CHECK_ERROR]: {
    icon: EligibilityErrorIcon,
    text: "No accreditation information",
  },
  [NeverEverEligibilityStatus.UNKNOWN]: {
    icon: null,
    text: " No accreditation information",
  },
};

export function EligiblityStatus({
  eligibilityStatus,
  showTextStatus = false,
}: EligibilityStatusProps): React.JSX.Element {
  const mapping = IconTextMap[eligibilityStatus];
  const { icon: Icon, text } =
    mapping || IconTextMap[NeverEverEligibilityStatus.UNKNOWN];
  return (
    <>
      {Icon && <Icon />} {showTextStatus && text}
    </>
  );
}

export interface NeverEverIntegrationStatusProps {
  consignmentId: string;
  showNoRequests?: boolean;
  showGenericTooltip?: boolean;
  showTextStatus?: boolean;
}

function getTooltip(
  showGenericTooltip: boolean,
  eligibilityStatus: NeverEverEligibilityStatus,
  neeAccreditationNotes: string | null,
): string {
  if (showGenericTooltip) {
    if (eligibilityStatus === NeverEverEligibilityStatus.UNKNOWN) {
      return "Accreditation not yet requested. Request on the Consignment to enable.";
    }
    if (
      eligibilityStatus === NeverEverEligibilityStatus.ELIGIBILITY_CHECK_ERROR
    ) {
      return "Accreditation request failed. Try requesting again via Consignment.";
    }
  } else if (eligibilityStatus === NeverEverEligibilityStatus.UNKNOWN) {
    // eslint and prettier are at odds with how they want this string formatted.
    return `Tap "${"Request NEE accreditation"}" to fetch your information from Never Ever and enable the option to show NEE on your Sale Lots`;
  }

  // accreditation notes may be null, convert to empty string so return value is always string
  return neeAccreditationNotes || "";
}

export function NeverEverIntegrationStatus(
  props: NeverEverIntegrationStatusProps,
) {
  const {
    consignmentId,
    showNoRequests = false,
    showGenericTooltip = false,
    showTextStatus = false,
  } = props;

  const eligibilityStatus: NeverEverEligibilityStatus = useSelector(
    getNeverEverEligibilityStatusByConsignmentId(consignmentId),
  );
  const consignment =
    useSelector(getConsignmentById(consignmentId)) || EMPTY_OBJECT;

  const { lastNeeAccreditationCheck, neeAccreditationNotes } = consignment;

  const tooltip = getTooltip(
    showGenericTooltip,
    eligibilityStatus,
    neeAccreditationNotes,
  );
  return (
    <Column alignItems="center">
      <Row alignItems="center">
        <span>
          <Typography component="span">Never Ever</Typography>
          <EligiblityStatus
            eligibilityStatus={eligibilityStatus}
            showTextStatus={showTextStatus}
          />
          {tooltip && (
            <span className="p-2">
              <Tooltip title={tooltip} />
            </span>
          )}
        </span>
      </Row>
      {eligibilityStatus === NeverEverEligibilityStatus.UNKNOWN &&
        showNoRequests && (
          <Row alignItems="center">
            <SmallText>No requests made today</SmallText>
          </Row>
        )}
      {eligibilityStatus !== NeverEverEligibilityStatus.UNKNOWN && (
        <Row alignItems="center">
          <SmallText>
            Last Checked{" "}
            {formatUTCToLocalDateTimeString(lastNeeAccreditationCheck)}
          </SmallText>
        </Row>
      )}
      {eligibilityStatus ===
        NeverEverEligibilityStatus.ELIGIBILITY_CHECK_ERROR && (
        <Row alignItems="center">
          <SmallText>
            {NeverEverEligibilityStatusDisplayNameMap[eligibilityStatus]}
          </SmallText>
        </Row>
      )}
    </Column>
  );
}
